/**
 * O que faz: Valida respostas de métricas com Zod estrito (desconhecido nunca vira zero).
 * Onde usa: src/services/metricasService após cada RPC de domínio.
 * Conexões: zod; tipos de supabase/functions/_shared/metricas/contratos.
 * Tabelas: Nenhuma (validação em memória).
 * Data: 05/09/2026.
 */

import { z } from 'zod';
import type { DominioMetricas } from '../../../supabase/functions/_shared/metricas/contratos';

// Data/hora precisa ser ISO legível; texto livre não pode virar período válido.
function esquemaDataISO(descricao: string) {
  return z.string().refine((texto) => Number.isFinite(Date.parse(texto)), {
    message: `${descricao} precisa de data ISO válida.`,
  });
}

const esquemaPeriodo = z.object({
  inicio: esquemaDataISO('Início do período'),
  fim: esquemaDataISO('Fim do período'),
}).strict();

const esquemaIndicador = z.object({
  id: z.string().regex(/^[a-z][a-z0-9_]*$/, 'ID do indicador precisa de snake_case estável.'),
  valor: z.number().finite().nonnegative().nullable(),
  unidade: z.enum(['contagem', 'percentual', 'centavos', 'segundos', 'razao']),
  numerador: z.number().finite().nonnegative().nullable(),
  denominador: z.number().finite().nonnegative().nullable(),
  amostra: z.number().int().nonnegative(),
  estado: z.enum(['available', 'partial', 'unavailable']),
  motivo: z.string().nullable(),
  melhorQuando: z.enum(['sobe', 'desce', 'neutro']),
  populacao: z.enum(['coorte_lead', 'coorte_envio', 'eventos_periodo', 'estoque_atual']),
}).strict().superRefine((indicador, ctx) => {
  // Disponível sem valor esconde falha, exceto taxa sem base: denominador zero explica o nulo.
  if (indicador.estado === 'available' && indicador.valor === null && indicador.denominador !== 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Indicador disponível precisa de valor.' });
  }
  if (indicador.estado === 'unavailable' && (indicador.valor !== null || !indicador.motivo)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Indicador indisponível precisa de motivo e valor nulo.' });
  }
  // Taxa percentual fora de 0-100 indica numerador/denominador inconsistentes.
  if (indicador.unidade === 'percentual' && indicador.valor !== null && indicador.valor > 100) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Percentual precisa estar entre 0 e 100.' });
  }
});

const esquemaResposta = z.object({
  schema_version: z.literal('1'),
  definition_version: z.string().min(1),
  dominio: z.enum(['comercial', 'followup', 'disparos', 'atendimento', 'operacao', 'agentes']),
  periodo: esquemaPeriodo,
  timezone: z.literal('America/Sao_Paulo'),
  observed_until: esquemaDataISO('Observado até'),
  available_from: esquemaDataISO('Disponível desde').nullable(),
  source_updated_at: esquemaDataISO('Fonte atualizada em').nullable(),
  attribution_window_days: z.number().int().nonnegative().nullable(),
  currency: z.string().nullable(),
  estado: z.enum(['available', 'partial', 'unavailable']),
  limitacoes: z.array(z.string()),
  indicadores: z.array(esquemaIndicador).min(1).superRefine((lista, ctx) => {
    // IDs duplicados quebram gráficos e detalhes que buscam por identificador.
    const vistos = new Set<string>();
    for (const item of lista) {
      if (vistos.has(item.id)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `ID duplicado: ${item.id}.` });
      }
      vistos.add(item.id);
    }
  }),
  anterior: z.object({
    periodo: esquemaPeriodo,
    indicadores: z.array(esquemaIndicador),
  }).strict().nullable(),
  serie: z.array(z.object({
    inicio: esquemaDataISO('Início da faixa'),
    fim: esquemaDataISO('Fim da faixa'),
    valores: z.record(z.number().finite().nullable()),
  }).strict()).max(90),
  ranking: z.array(z.object({
    id: z.string().min(1),
    nome: z.string().min(1),
    indicadores: z.array(esquemaIndicador),
  }).strict()).max(10),
  cobertura: z.object({
    vinculados: z.number().int().nonnegative(),
    elegiveis: z.number().int().nonnegative(),
    percentual: z.number().finite().nonnegative().nullable(),
  }).strict(),
}).strict();

export function validarRespostaMetricas(raw: unknown, dominio: DominioMetricas) {
  // Domínio divergente indica cache trocado ou RPC errada; nunca aceitar como sucesso.
  const parsed = esquemaResposta.parse(raw);
  if (parsed.dominio !== dominio) {
    throw new Error(`Domínio divergente: esperado ${dominio}, recebido ${parsed.dominio}.`);
  }
  return parsed;
}
